import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { IFinancingOption, ILocalizedInformation } from '@inter-ikea-kompis/types';
import type ITheme from '@inter-ikea-kompis/types/lib/types/theme/ITheme.js';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';

import type IFinancialServicesTranslations from '../../types/IFinancialServicesTranslations.js';
import FinancialServicesButtonsStyle from './FinancialServicesButtonsStyle.js';

/**
 * Financial services buttons.
 *
 * @event continueButtonClick When continue planning button is clicked.
 * @event moreInfoClick When read more is clicked.
 */

export default class FinancialServicesButtons extends KompisElement {
	// Financing option.
	@property({ type: Object, required: true })
	public financingOption!: IFinancingOption;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IFinancialServicesTranslations | null = null;

	// Disables links. Shows QR code. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${FinancialServicesButtonsStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="buttonContainer">
				${this.getStandardConfiguration()} ${this.getKioskConfiguration()}
			</div>
		`;
	}
	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-qr-code', QrCode);

		super.connectedCallback();
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IFinancialServicesTranslations {
		return this.translations
			? this.translations
			: <IFinancialServicesTranslations>this.localizedInformation?.translations;
	}

	private getStandardConfiguration(): RenderResult {
		if (this.disableLinks) {
			return null;
		}
		return html`${this.getMoreInfoLink()} ${this.getContinueButton()}`;
	}

	private getKioskConfiguration(): RenderResult {
		if (!this.disableLinks) {
			return null;
		}

		return html`${this.getQrCode()} ${this.getContinueButton()}`;
	}

	private getContinueButton(): RenderResult {
		const primaryVariant: ButtonVariant = 'primary';

		return html`<skapa-button
			variant="${primaryVariant}"
			class="continue-button"
			@click="${() => {
				this.dispatchEvent(new CustomEvent('continueButtonClick'));
			}}"
			fluid
		>
			${this.getTranslations().financialServicesContinueButton}
		</skapa-button>`;
	}

	private getMoreInfoLink(): RenderResult {
		const secondaryVariant: ButtonVariant = 'secondary';

		return html`
			<skapa-button
				class="info-button"
				new-window
				href="${this.financingOption.moreInfoLink}"
				variant="${secondaryVariant}"
				@click="${() => {
					this.dispatchEvent(new CustomEvent('moreInfoClick'));
				}}"
				fluid
			>
				${this.financingOption.moreInfoAnchor}
			</skapa-button>
		`;
	}

	private getQrCode(): RenderResult {
		return html`<div class="qr-code">
			<kompis-qr-code .url="${this.financingOption.moreInfoLink}"></kompis-qr-code>
			<kompis-text class="qr-code-text" .theme="${this.theme}">
				${this.financingOption.moreInfoAnchor}
			</kompis-text>
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'kompis-financial-services-buttons': FinancialServicesButtons;
	}
}
